import * as XLSX from 'xlsx';
import { formatDate } from './formatters';

const fmtCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

function buildBalanceSheetData(balanceSheet, llcName, balanceAsOfDate) {
  const wsData = [];
  wsData.push([llcName]);
  wsData.push(['Balance Sheet']);
  wsData.push([`As of ${formatDate(balanceAsOfDate)}`]);
  wsData.push([]);
  
  wsData.push(['ASSETS', '']);
  wsData.push([]);
  
  if (balanceSheet.current_assets && Object.keys(balanceSheet.current_assets).length > 0) {
    wsData.push(['Current Assets', '']);
    Object.entries(balanceSheet.current_assets).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Current Assets', fmtCurrency(balanceSheet.total_current_assets)]);
    wsData.push([]);
  }
  
  if (balanceSheet.fixed_assets && Object.keys(balanceSheet.fixed_assets).length > 0) {
    wsData.push(['Fixed Assets', '']);
    Object.entries(balanceSheet.fixed_assets).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Fixed Assets', fmtCurrency(balanceSheet.total_fixed_assets)]);
    wsData.push([]);
  }
  
  if (balanceSheet.other_assets && Object.keys(balanceSheet.other_assets).length > 0) {
    wsData.push(['Other Assets', '']);
    Object.entries(balanceSheet.other_assets).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Other Assets', fmtCurrency(balanceSheet.total_other_assets)]);
    wsData.push([]);
  }
  
  wsData.push(['TOTAL ASSETS', fmtCurrency(balanceSheet.total_assets)]);
  wsData.push([]);
  
  wsData.push(['LIABILITIES', '']);
  wsData.push([]);
  
  if (balanceSheet.current_liabilities && Object.keys(balanceSheet.current_liabilities).length > 0) {
    wsData.push(['Current Liabilities', '']);
    Object.entries(balanceSheet.current_liabilities).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Current Liabilities', fmtCurrency(balanceSheet.total_current_liabilities)]);
    wsData.push([]);
  }
  
  if (balanceSheet.long_term_liabilities && Object.keys(balanceSheet.long_term_liabilities).length > 0) {
    wsData.push(['Long-term Liabilities', '']);
    Object.entries(balanceSheet.long_term_liabilities).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Long-term Liabilities', fmtCurrency(balanceSheet.total_long_term_liabilities)]);
    wsData.push([]);
  }
  
  wsData.push(['TOTAL LIABILITIES', fmtCurrency(balanceSheet.total_liabilities)]);
  wsData.push([]);
  
  if (balanceSheet.equity && Object.keys(balanceSheet.equity).length > 0) {
    wsData.push(['EQUITY', '']);
    Object.entries(balanceSheet.equity).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
    wsData.push(['Total Equity', fmtCurrency(balanceSheet.total_equity)]);
    wsData.push([]);
  }
  
  wsData.push(['TOTAL LIABILITIES & EQUITY', fmtCurrency(balanceSheet.total_liabilities_and_equity)]);
  return wsData;
}

function buildIncomeStatementData(incomeStatement, llcName, startDate, endDate) {
  const wsData = [];
  wsData.push([llcName]);
  wsData.push(['Income Statement']);
  wsData.push([`Period: ${formatDate(startDate)} to ${formatDate(endDate)}`]);
  wsData.push([]);
  
  wsData.push(['INCOME', '']);
  if (incomeStatement.income && Object.keys(incomeStatement.income).length > 0) {
    Object.entries(incomeStatement.income).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
  }
  wsData.push(['Total Income', fmtCurrency(incomeStatement.total_income)]);
  wsData.push([]);
  
  wsData.push(['EXPENSES', '']);
  if (incomeStatement.expenses && Object.keys(incomeStatement.expenses).length > 0) {
    Object.entries(incomeStatement.expenses).forEach(([name, amount]) => {
      wsData.push([`  ${name}`, fmtCurrency(amount)]);
    });
  }
  wsData.push(['Total Expenses', fmtCurrency(incomeStatement.total_expenses)]);
  wsData.push([]);
  
  wsData.push(['NET INCOME', fmtCurrency(incomeStatement.net_income)]);
  return wsData;
}

function buildGeneralLedgerData(generalLedger, llcName, startDate, endDate) {
  const wsData = [];
  wsData.push([llcName]);
  wsData.push(['General Ledger']);
  wsData.push([`Period: ${formatDate(startDate)} to ${formatDate(endDate)}`]);
  wsData.push([]);
  
  const accountTypeNames = {
    'current_asset': 'CURRENT ASSETS',
    'fixed_asset': 'FIXED ASSETS',
    'other_asset': 'OTHER ASSETS',
    'current_liability': 'CURRENT LIABILITIES',
    'long_term_liability': 'LONG-TERM LIABILITIES',
    'equity': 'EQUITY',
    'income': 'INCOME',
    'expense': 'EXPENSES'
  };
  
  let currentType = '';
  
  generalLedger.accounts.forEach(account => {
    if (account.account_type !== currentType) {
      currentType = account.account_type;
      wsData.push([]);
      wsData.push([accountTypeNames[currentType] || currentType, '', '', '', '']);
    }
    
    wsData.push([]);
    wsData.push([account.account_name, '', '', '', '', '']);
    wsData.push(['Date', 'Description', 'Vendor/Payee', 'Ref', 'Debit', 'Credit']);
    
    account.transactions.forEach(txn => {
      wsData.push([
        txn.date,
        txn.description || '-',
        txn.vendor_name || '-',
        txn.reference || '-',
        txn.debit ? fmtCurrency(txn.debit) : '',
        txn.credit ? fmtCurrency(txn.credit) : ''
      ]);
    });
    
    wsData.push([
      '',
      '',
      '',
      'Account Total:',
      fmtCurrency(account.total_debits),
      fmtCurrency(account.total_credits)
    ]);
  });
  
  wsData.push([]);
  wsData.push(['', '', 'GRAND TOTALS:', fmtCurrency(generalLedger.grand_total_debits), fmtCurrency(generalLedger.grand_total_credits)]);
  return wsData;
}

export function exportReportToExcel({ reportType, llcName, balanceSheet, incomeStatement, generalLedger, startDate, endDate, balanceAsOfDate }) {
  const reportTitle = reportType === 'balance' ? 'Balance Sheet' : (reportType === 'income' ? 'Income Statement' : 'General Ledger');
  
  const wb = XLSX.utils.book_new();
  let wsData = [];
  
  if (reportType === 'balance' && balanceSheet) {
    wsData = buildBalanceSheetData(balanceSheet, llcName, balanceAsOfDate);
  } else if (reportType === 'income' && incomeStatement) {
    wsData = buildIncomeStatementData(incomeStatement, llcName, startDate, endDate);
  } else if (reportType === 'ledger' && generalLedger) {
    wsData = buildGeneralLedgerData(generalLedger, llcName, startDate, endDate);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  
  if (reportType === 'ledger') {
    ws['!cols'] = [
      { wch: 12 },
      { wch: 35 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 }
    ];
  } else {
    ws['!cols'] = [
      { wch: 35 },
      { wch: 18 }
    ];
  }
  
  XLSX.utils.book_append_sheet(wb, ws, reportTitle);
  
  const dateStr = reportType === 'balance' 
    ? balanceAsOfDate.replace(/-/g, '')
    : `${startDate.replace(/-/g, '')}-${endDate.replace(/-/g, '')}`;
  const filename = `${llcName.replace(/[^a-zA-Z0-9]/g, '_')}_${reportTitle.replace(' ', '_')}_${dateStr}.xlsx`;
  
  XLSX.writeFile(wb, filename);
  return filename;
}
